<template>
  <section class="grid grid-cols-12 gap-5 md:my-16 mt-8">
    <div id="header" class="col-span-12 md:col-span-9 flex flex-col gap-5">
      <div class="flex justify-between gap-3 items-center">
        <h3>
          {{
            $t("labels.generic", {
              pt: "Analisar palavras chaves",
              en: "Analyze keywords",
            })
          }}
        </h3>
        <div class="flex items-center gap-3">
          <Button
            class="btn-outline p-button-sm h-10"
            :label="$t('labels.cancel')"
            @click="confirmModal"
          />
          <Button
            class="btn-primary p-button-sm h-10"
            :label="$t('labels.save')"
            type="submit"
            @click="submit"
          />
        </div>
      </div>
      <div class="flex items-center gap-5 flex-wrap">
        <p>
          <strong>
            {{ $t("labels.generic", { pt: "Capítulo:", en: "Chapter:" }) }}
          </strong>
          {{ course?.chapter }}
        </p>
        <p>
          <strong>
            {{ $t("labels.generic", { pt: "Objetivo:", en: "Objective:" }) }}
          </strong>
          {{ course?.objective }}
        </p>
      </div>
    </div>

    <InfiniteScroll class="col-span-12 md:col-span-9">
      <template #header>
        <div class="p-5">
          <h5>
            {{
              $t("labels.generic", {
                pt: "Clique para remover as palavras-chaves geradas",
                en: "Click to remove generated keywords",
              })
            }}
          </h5>
        </div>
      </template>
      <template #content>
        <div
          v-if="!loader && data"
          class="flex items-center gap-5 flex-wrap p-5"
        >
          <div
            id="content"
            class="select-none"
            v-for="(item, i) in data"
            :key="i"
          >
            <Keywords
              v-model:selected="selected"
              :item="item"
              @onSelect="selectKeyword"
            />
          </div>
        </div>
        <EmptyData
          :data="(!data || data.length === 0) && !loader"
          :title="emptyState.title"
          :label="emptyState.description"
          :image="emptyState.image"
          imgSize="h-40"
        />
        <Loader :loader="loader" loaderSize="h-full" />
      </template>
    </InfiniteScroll>

    <ConfirmDialog />
  </section>
</template>

<script lang="ts">
import { CrawlerRest } from "@/services/rest/crawler.rest";
import { Messages } from "@/utils/messages.util";
import {
  getItemLocalStorage,
  removeItemLocalStorage
} from "@/utils/save-storage.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { debounceTime, last, take } from "rxjs";
import { defineAsyncComponent, defineComponent } from "vue";
import { BlacklistService } from "./blacklist.service";

const Keywords = defineAsyncComponent(() => import("./keywords.vue"));

export default defineComponent({
  name: "blackList",
  components: { Keywords },
  computed: {
    service(): BlacklistService {
      return new BlacklistService();
    },
    rest(): CrawlerRest {
      return new CrawlerRest();
    },
    course(): any {
      return JSON.parse(getItemLocalStorage("blacklistData"));
    },
  },
  data() {
    return {
      data: [] as any[],
      courseData: {} as any,
      emptyState: {
        image: "empty-blacklist.svg",
        title: this.$t("blacklist.empty.title"),
        description: this.$t("blacklist.empty.description"),
      } as any,
      selected: [] as any[],
      loader: true as boolean,
      objectiveId: String(this.$route.params.objectiveId),
    };
  },
  mounted() {
    if (this.course) {
      this.getKeywordsByObjective();
    } else {
      this.$router.go(-1);
    }
  },
  watch: {
    $route() {
      removeItemLocalStorage("blacklistData");
    },
  },
  methods: {
    getKeywordsByObjective(): void {
      this.service.keywords
        .pipe(take(2), last(), debounceTime(2000))
        .subscribe({
          next: (response) => {
            this.data = response;
            this.loader = false;
          },
        });
      this.service.getKeywordsByObjective(this.objectiveId);
    },
    submit(): void {
      this.loader = true;
      const keywords = this.selected.map((item) => {
        item.isEval = item.isEval === true ? -1 : 1;
        return item;
      });

      this.rest.addKeywordsToBlacklist({ keywords }).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.BLACKLIST_UPDATED));
          this.$router.go(-1);
        },
        error: (error) => {
          console.error(error);
        },
      });
    },
    selectKeyword(item): void {
      item.isEval = !item.isEval;
    },
    confirmModal(event?: any): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Ao ativar, todos os cursos e usuários associados voltarão a ter acesso a essa turma.`,
          en: `When deactivating the “Juventude Digital” class, all users and associated courses will no longer have access to this class.`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair da análise das palavras-chaves?",
          en: "Exit keyword analysis?",
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Sim, quero sair",
          en: "Yes, I want to leave",
        }),
        rejectLabel: this.$t("labels.generic", {
          pt: "Não, vou continuar",
          en: "No, I will continue",
        }),
        rejectClass: "btn-outline",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.$router.go(-1);
        },
      });
    },
  },
});
</script>
